import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Droplets, Beaker, TrendingUp, ArrowDown, ExternalLink, AlertTriangle, Lightbulb, Target, Zap } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import CropCTASection from '../components/CropCTASection';

const PistachioPage = () => {

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Pistachio Success: Balancing pH for Maximum Yield"
        description="Research-backed pH management for profitable pistachio production. Learn how water quality affects your pistachio orchard yield and quality."
        canonicalPath="/pistachio"
      />
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen md:min-h-[70vh] flex items-center justify-center overflow-hidden pt-16 pb-8">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="/Pistachios.jpg"
            alt="Pistachio orchard with trees bearing nuts"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-green-900/80 via-emerald-800/70 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center py-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-4 md:mb-6"
            >
              <span className="text-xl">🥜</span>
              <span>Premium Orchard Resource</span>
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight">
              Pistachio Success:
              <span className="block text-lime-300">Balancing pH for Maximum Yield</span>
            </h1>
            
            <p className="text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-white/90 leading-relaxed max-w-4xl mx-auto">
              Research-backed pH management for profitable pistachio production
            </p>

            <motion.button
              onClick={() => document.getElementById('intro-content')?.scrollIntoView({ behavior: 'smooth' })}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-lime-500 text-white px-8 md:px-12 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl hover:bg-lime-600 transition-colors inline-flex items-center space-x-3 shadow-lg mb-6 md:mb-8"
            >
              <span>Start Reading Guide</span>
              <ArrowDown size={20} className="md:w-6 md:h-6" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Intro Content */}
      <section id="intro-content" className="py-16 md:py-20 bg-gradient-to-br from-lime-50 to-green-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-lime-100 text-lime-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="text-xl">🥜</span>
              <span>Introduction</span>
            </div>
            
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-lime-200">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                <strong>Pistachios have specific soil pH requirements</strong> — and your irrigation water could be quietly undermining your orchard's productivity and nut quality season after season.
              </p>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mt-4">
                At CTC Waterworks, we've helped pistachio growers optimize their water quality for 40+ years. This guide explains why your irrigation water pH matters for pistachios, what research shows, and how sulfur burners can protect your soil, enhance your nut quality, and maximize your profits.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Research-Based Content Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 space-y-16">
          
          {/* Section 1: Ideal Soil pH for Pistachios */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-lime-500 to-green-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🔍</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                1: Ideal Soil pH for Pistachios
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-lime-50 to-green-50 rounded-xl p-6 md:p-8 border border-lime-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-lime-500 pl-6">
                "Pistachios prefer well-drained soils with a slightly alkaline to neutral pH, typically between 7.0–7.8. However, excessive soil pH (&gt;8) can cause micronutrient deficiencies, especially iron and zinc."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-lime-600" size={16} />
                <span className="text-sm font-medium text-lime-600">
                  Source: UC ANR — Pistachio Production Manual (ANR Pub 3545)
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Target className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">While pistachios tolerate slightly alkaline soils, irrigation water with high bicarbonates can gradually push pH too high — tying up nutrients and increasing your need for costly amendments like iron chelates.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 2: Hidden Problem - High Bicarbonate Irrigation Water */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">💧</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                2: Hidden Problem — High Bicarbonate Irrigation Water
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 md:p-8 border border-orange-200">
              <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
                Think of bicarbonates as tiny pH "elevators" in your water. When you irrigate with high-bicarbonate water, 
                these compounds slowly but steadily raise soil pH. It's like adding lime to your 
                pistachio orchard, drop by drop, season after season. Even if your soil pH starts in the optimal range, 
                alkaline irrigation water can gradually push it too high, affecting nutrient availability.
              </p>

              <blockquote className="text-lg text-gray-700 mb-6 italic border-l-4 border-orange-500 pl-6 bg-white rounded-lg p-4">
                "Bicarbonate levels in irrigation water are one of the main factors that cause soil pH to increase over time. As pH rises, pistachios may exhibit iron chlorosis and poor nut fill."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-orange-600" size={16} />
                <span className="text-sm font-medium text-orange-600">
                  Source: University of Arizona Cooperative Extension — Salinity and Pistachio Production
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">If your irrigation water tests over ~120 ppm bicarbonates, inline acidification is an effective way to prevent pH drift — so you don't spend more every year battling nutrient deficiencies.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 3: Impact on Drip & Microirrigation */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🚜</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                3: Impact on Drip & Microirrigation
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-blue-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-blue-500 pl-6">
                "Water high in bicarbonates or carbonates can precipitate as scale in pipelines and emitters, resulting in partial or complete plugging of microirrigation systems."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-blue-600" size={16} />
                <span className="text-sm font-medium text-blue-600">
                  Source: UC ANR — Agricultural Salinity and Drainage (FAO Soils Bulletin 60)
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Zap className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">When plugged emitters go unnoticed, parts of your orchard get too little water — reducing nut size and uniformity. Inline acidification helps keep your system flowing freely.</p>
              </div>

              {/* Problem vs Solution Visual */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3">❌ High-Bicarbonate Water Problems:</h4>
                  <ul className="space-y-2 text-sm text-red-700">
                    <li>• Clogged drip emitters</li>
                    <li>• Uneven water distribution</li>
                    <li>• Reduced nut size</li>
                    <li>• Inconsistent quality</li>
                  </ul>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3">✅ With pH Management:</h4>
                  <ul className="space-y-2 text-sm text-green-700">
                    <li>• Clean, efficient emitters</li>
                    <li>• Uniform water application</li>
                    <li>• Consistent nut size</li>
                    <li>• Better split percentage</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 4: Soil & Water Management for Pistachios */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🌱</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                4: Soil & Water Management for Pistachios
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 md:p-8 border border-green-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-green-500 pl-6">
                "Monitoring irrigation water quality and soil pH is critical to managing salinity and sodicity in pistachio orchards. Acidifying high-bicarbonate water can maintain infiltration rates and nutrient availability."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-green-600" size={16} />
                <span className="text-sm font-medium text-green-600">
                  Source: UCANR — Salinity Management in Pistachio Orchards
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Lightbulb className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Good orchard managers don't wait until they see yellow leaves — they test water every season and adjust pH before problems show up.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 5: Bicarbonate Threshold */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">📊</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                5: Bonus — Bicarbonate Threshold
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 md:p-8 border border-teal-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-teal-500 pl-6">
                "If bicarbonate levels exceed 120 ppm, neutralization is recommended to maintain soil structure, water infiltration, and orchard productivity."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-teal-600" size={16} />
                <span className="text-sm font-medium text-teal-600">
                  Source: UCANR — Salinity Management & Reclamation
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">When you keep bicarbonates under control, you protect your trees, your drip system, and your yields.</p>
              </div>
              
              <div className="bg-white rounded-lg p-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Why Choose CTC Sulfur Burners for Pistachios:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    'Supports root-zone pH stability by neutralizing bicarbonates in irrigation water',
                    'Prevents emitter clogging in drip systems',
                    'Safer than handling liquid acids',
                    'Provides sulfur nutrition as a bonus',
                    'Automated operation with minimal maintenance',
                    '40+ years of proven results in nut orchards'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <CropCTASection cropName="Pistachios" />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PistachioPage;